import { classSet } from '@platform/ui-library';
import {
  FC,
  FunctionComponent,
  ReactNode
} from 'react';
import { Message, MessageProps, MessageStatus } from '@app/form_components';
import { DistributiveOmit } from '@app/types';
import { Label, LabelProps } from '../label';
import styles from './form-field.module.scss';

export const FormFieldClassName = {
  labelLeft: styles.labelLeft
} as const;

/** Wrapper for form component with standard CSS, and options. */
export type FormFieldProps = (
  Pick<LabelProps, 'htmlFor' | 'tooltip'>
  & {
    children: ReactNode;
    className?: string;
    /** Errors on the control. */
    error?: MessageProps['message'];
    /** Label describing the field. */
    label: string;
    size?: 'small';
  }
);

export const FormField: FC<FormFieldProps> = ({
  className,
  children,
  error,
  htmlFor,
  label,
  size,
  tooltip
}) => {
  className = classSet([
    styles.formField,
    (size) ? styles[size] : null,
    className
  ]);
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} tooltip={tooltip}>
        {label}
      </Label>
      <div className={styles.subContainer}>
        {children}
      </div>
      <Message message={error} status={MessageStatus.error} />
    </div>
  );
};

export type FormFieldControlProps = (
  Pick<FormFieldProps, 'error' | 'label' | 'tooltip'>
  & {
    fieldConfig?: Pick<FormFieldProps, 'className' | 'size'>;
  }
);

export type ControlWithFormFieldProps<TControlProps> = (
  FormFieldControlProps
  & TControlProps
  & {
    control: FunctionComponent<
      DistributiveOmit<TControlProps, 'control' | keyof(FormFieldControlProps)>
    >;
  }
);

export function ControlWithFormField<TControlProps>({
  control,
  error,
  fieldConfig,
  label,
  tooltip,
  ...controlProps
}: ControlWithFormFieldProps<TControlProps>): ReturnType<FC> {
  const Control = control as FunctionComponent<typeof controlProps>;
  const controlName = (
    'name' in controlProps
    && typeof controlProps.name === 'string'
  ) ? controlProps.name : null;

  return (
    <FormField
      error={error}
      label={label}
      htmlFor={controlName}
      className={fieldConfig?.className}
      size={fieldConfig?.size}
      tooltip={tooltip}
    >
      <Control {...controlProps} />
    </FormField>
  );
};
